<script setup lang="ts">
import { computed, defineProps } from "vue";

interface Props {
  current: number;
  target: number | null;
}

const props = defineProps<Props>();

const normalizedTarget = computed(() => {
  const target = props.target ?? 0;
  return target > 0 ? target : 0;
});

const hasTarget = computed(() => normalizedTarget.value > 0);

const progressPercentage = computed(() => {
  if (!hasTarget.value) return 0;
  return Math.min(Math.round((props.current / normalizedTarget.value) * 100), 100);
});

const remaining = computed(() => {
  if (!hasTarget.value) return 0;
  return Math.max(normalizedTarget.value - props.current, 0);
});

const progressColorClass = computed(() => {
  if (progressPercentage.value >= 100) return "bg-green-500";
  if (progressPercentage.value >= 70) return "bg-amber-500";
  return "bg-primary";
});

const percentageTextClass = computed(() => {
  if (progressPercentage.value >= 100) return "text-green-700";
  if (progressPercentage.value >= 70) return "text-amber-700";
  return "text-primary";
});

const helperText = computed(() => {
  if (!hasTarget.value) {
    return "Target amount is missing. Configure random weight target to track progress.";
  }

  if (remaining.value === 0) {
    return "Sampling target reached.";
  }

  const suffix = remaining.value === 1 ? "" : "s";
  return `Record ${remaining.value} more sample${suffix} to complete random sampling.`;
});
</script>

<template>
  <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
    <div class="flex items-start justify-between gap-4">
      <div>
        <h3 class="text-sm font-semibold text-gray-900">Random Weights Progress</h3>
        <p class="text-xs text-gray-600 mt-0.5">
          {{ current }} / {{ hasTarget ? normalizedTarget : "?" }} recorded
        </p>
      </div>
      <span class="text-sm font-semibold" :class="percentageTextClass">
        {{ progressPercentage }}%
      </span>
    </div>

    <div class="mt-3 w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
      <div
        class="h-2.5 rounded-full transition-all duration-500 ease-out"
        :class="progressColorClass"
        :style="{ width: `${progressPercentage}%` }"
      ></div>
    </div>

    <p class="mt-2 text-xs text-gray-600">
      {{ helperText }}
    </p>
  </div>
</template>
